import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import type { ReactNode } from 'react';

import {
  SELECTED_COUNTRY_UK,
  SELECTED_COUNTRY_USA,
} from '@lib/utils/constants/selectedCountry';

type Props = {
  children: ReactNode;
};

type ContextValue = {
  currentPage: SignupPageType;
  setCurrentPage: ReactStateUpdater<SignupPageType>;
  selectedCountry: SelectedCountryType;
  setSelectedCountry: ReactStateUpdater<SelectedCountryType>;
  isUkSelected: boolean;
  isUsaSelected: boolean;
  goToCountrySelection: () => void;
  birthDateString: string | undefined;
  setBirthDateString: ReactStateUpdater<string | undefined>;
  isChangeCountryEnabled: boolean;
  setIsChangeCountryEnabled: ReactStateUpdater<boolean>;
  tempUserId: number | null;
  setTempUserId: ReactStateUpdater<number | null>;
};

const SignupPageContext = createContext({
  currentPage: 'country_selection',
  selectedCountry: null,
  isUkSelected: false,
  isUsaSelected: false,
  isChangeCountryEnabled: true,
} as ContextValue);

export type SignupPageType = 'account_creation' | 'country_selection';

export const SignupPageProvider = ({ children }: Props): JSX.Element => {
  const [birthDateString, setBirthDateString] = useState<string | undefined>();
  const [currentPage, setCurrentPage] =
    useState<SignupPageType>('country_selection');

  const [isChangeCountryEnabled, setIsChangeCountryEnabled] =
    useState<boolean>(true);

  const [tempUserId, setTempUserId] = useState<number | null>(null);

  const [selectedCountry, setSelectedCountry] =
    useState<SelectedCountryType>(null);

  const isUkSelected = useMemo(
    () => selectedCountry === SELECTED_COUNTRY_UK,
    [selectedCountry]
  );
  const isUsaSelected = useMemo(
    () => selectedCountry === SELECTED_COUNTRY_USA,
    [selectedCountry]
  );

  const goToCountrySelection = useCallback(
    () => setCurrentPage('country_selection'),
    []
  );

  return (
    <SignupPageContext.Provider
      value={{
        currentPage,
        setCurrentPage,
        selectedCountry,
        setSelectedCountry,
        isUkSelected,
        isUsaSelected,
        goToCountrySelection,
        birthDateString,
        setBirthDateString,
        isChangeCountryEnabled,
        setIsChangeCountryEnabled,
        tempUserId,
        setTempUserId,
      }}
    >
      {children}
    </SignupPageContext.Provider>
  );
};

export const useSignupPageContext = (): ContextValue =>
  useContext<ContextValue>(SignupPageContext);
